import { Dayjs } from "dayjs";
import { isNil, isPlainObject, startCase } from "lodash";
import pluralize from "pluralize";
import { Audit, CatalogItemObject, PurchaseItemObject } from "types";
import { getValueWithCurrency } from "@/components/Common/Currency";
import { getSeller } from "@/utils/getSeller";

// No enum exists for the inventory-lot audit class; keep the literal in one place.
const WARE_INVENTORY_LOT = "ware_inventory_lot";

export const NULL_VALUE = "—";

// Default formatting: strings render verbatim so the report keeps exact old/new fidelity (a rename
// like "hoodie" -> "hoodie2" must not collapse to the same Title-cased value). Enum fields opt into
// Title-casing via FieldDef.format = formatEnum.
const formatValue = (value: unknown): string => {
    if (isNil(value) || value === "") {
        return NULL_VALUE;
    }

    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }

    if (typeof value === "number") {
        return String(value);
    }

    if (typeof value === "string") {
        return value;
    }

    if (Array.isArray(value)) {
        return value.length > 0 ? value.map((item) => formatValue(item)).join(", ") : NULL_VALUE;
    }

    return JSON.stringify(value);
};

// Enum-token formatter: lowercase / snake_case tokens render Title-cased ("require_availability" ->
// "Require Availability"). Opt-in per field; everything else defers to verbatim default formatting.
const formatEnum = (value: unknown): string => {
    if (Array.isArray(value)) {
        return value.length > 0 ? value.map((item) => formatEnum(item)).join(", ") : NULL_VALUE;
    }

    if (typeof value === "string" && /^[a-z\d_]+$/.test(value)) {
        return startCase(value);
    }

    return formatValue(value);
};

export type LedgerCategory = "Product" | "Catalog" | "Inventory Adjustment" | "Purchase";

export interface LedgerRow {
    auditId: ID;
    objectId: ID; // ware / ware_catalog_item / ware_inventory_lot id, resolved to a variant name for display
    category?: LedgerCategory; // set by the page (which audit source produced the row)
    date: Dayjs;
    userId?: ID;
    changeLabel: string;
    from: string;
    to: string;
    reason?: string; // inventory-lot data.reason.new (e.g. "Received", "Safety Stock")
    comment?: string; // inventory-lot data.comment.new (free-text note accompanying the reason)
    quantity?: number; // units moved for inventory-lot events (count/available/unavailable delta)
}

interface FieldDef {
    label: string;
    format?: (value: unknown) => string;
    fields?: Record<string, FieldDef>; // sub-field defs for nested objects (e.g. warePreference)
    hidden?: boolean; // internal-only fields (e.g. inventory/prices linkage), never emit a row
    hideWhenOldNull?: boolean; // skip the creation-time default set (old === null); show real changes
}

const formatDownDeposit = (value: unknown): string => {
    if (!isPlainObject(value)) {
        return NULL_VALUE;
    }

    const { amount, amountType, scope } = value as { amount?: number; amountType?: string; scope?: string };
    if (isNil(amount)) {
        return NULL_VALUE;
    }

    const formattedAmount =
        amountType === "percentage" ? `${amount}%` : getValueWithCurrency(amount, getSeller().currency);
    return scope ? `${formattedAmount} / ${scope}` : formattedAmount;
};

const formatPhoto = (value: unknown): string => (value ? "Image" : NULL_VALUE);

const formatMedias = (value: unknown): string => {
    if (!Array.isArray(value) || value.length === 0) {
        return NULL_VALUE;
    }

    return `${value.length} ${pluralize("photo", value.length)}`;
};

const formatAttachments = (value: unknown): string => {
    if (!Array.isArray(value) || value.length === 0) {
        return NULL_VALUE;
    }

    const titles = value
        .map((item) => (isPlainObject(item) ? (item as { title?: string }).title : undefined))
        .filter(Boolean);
    return titles.length > 0 ? titles.join(", ") : NULL_VALUE;
};

const formatPricePerUnit = (value: unknown): string => {
    if (!isPlainObject(value)) {
        return NULL_VALUE;
    }

    const { price } = value as { price?: number };
    return isNil(price) ? NULL_VALUE : getValueWithCurrency(price, getSeller().currency);
};

// Product-level `price` is a plain number, unlike the variant `prices.perUnit` object; render it with
// currency so both read alike instead of a bare "12.5".
const formatPrice = (value: unknown): string =>
    isNil(value) || value === "" ? NULL_VALUE : getValueWithCurrency(Number(value), getSeller().currency);

// Raw value, no Title-casing (SKU/barcode strings must render verbatim).
const formatRaw = (value: unknown): string => (isNil(value) || value === "" ? NULL_VALUE : String(value));

// "unlimited" reads as "Non Tracked" in the ledger; everything else is an enum token.
const formatInventoryPolicy = (value: unknown): string => (value === "unlimited" ? "Non Tracked" : formatEnum(value));

// Registry of known product-level fields. Absent keys fall back to a humanized label + default
// value formatting.
const LEDGER_FIELDS: Record<string, FieldDef> = {
    name: { label: "Title" },
    excerpt: { label: "Excerpt" },
    desc: { label: "Description" },
    terms: { label: "Terms" },
    price: { label: "Price", format: formatPrice },
    fulfilledAt: { label: "Fulfilled At", hidden: true },
    deletedBy: { label: "Deleted By", hidden: true },
    deletedAt: { label: "Deleted At", hidden: true },
    downDeposit: { label: "Down Deposit", format: formatDownDeposit },
    allowedSources: { label: "Available Channels", format: formatEnum },
    photo: { label: "Primary Photo", format: formatPhoto },
    medias: { label: "Photos", format: formatMedias },
    attachments: { label: "Attachments", format: formatAttachments },
    warePreference: {
        label: "Preferences",
        fields: {
            fulfillmentType: { label: "Fulfillment Type", format: formatEnum },
            refundPolicy: { label: "Refund Policy", format: formatEnum },
            revenueRecognitionPolicy: { label: "Revenue Recognition", format: formatEnum },
            fulfillmentPolicy: { label: "Fulfillment Policy", format: formatEnum },
            variantType: { label: "Variant Type", format: formatEnum },
            inventoryPolicy: { label: "Inventory Policy", format: formatInventoryPolicy },
        },
    },
};

// Inventory-level fields (objectClass ware_catalog_item / ware_inventory_lot),
// fetched via the context.id/context.object audit API.
const INVENTORY_FIELDS: Record<string, FieldDef> = {
    name: { label: "Name" },
    sku: { label: "SKU", format: formatRaw },
    barcode: { label: "Barcode", format: formatRaw },
    status: { label: "Status", format: formatEnum },
    available: { label: "Available" },
    unavailable: { label: "Unavailable" },
    reserved: { label: "Reserved" },
    pendingFulfillment: { label: "Committed" },
    count: { label: "On Hand", hidden: true },
    medias: { label: "Photos", format: formatMedias },
    pricePerUnit: { label: "Price", hidden: true }, // old side is always null; real change lives in `prices.perUnit`
    oversellPolicy: { label: "Oversell Policy", format: formatEnum, hideWhenOldNull: true },
    visibility: { label: "Visibility", format: formatEnum },
    inventory: { label: "Inventory", hidden: true }, // internal lot linkage
    prices: { label: "Prices" }, // {perUnit:{old,new}}, expanded to a single "Price" row
    template: { label: "Template", hidden: true }, // internal template linkage (code/type), not user-facing
    fulfilledAt: { label: "Fulfilled At", hidden: true },
    deletedBy: { label: "Deleted By", hidden: true },
    deletedAt: { label: "Deleted At", hidden: true },
    // Both ride along on lot audits and are surfaced as the row's own reason column, not as change rows.
    reason: { label: "Reason", hidden: true },
    comment: { label: "Comment", hidden: true },
};

// Catalog-item audits reuse the inventory fields, but `available` is a derived aggregate that just
// mirrors lot movements, so hide it (the real change shows on the lot).
const CATALOG_ITEM_FIELDS: Record<string, FieldDef> = {
    ...INVENTORY_FIELDS,
    available: { ...INVENTORY_FIELDS.available, hidden: true },
};

interface VariantGroup {
    name?: string;
    options?: Array<{ name?: string }>;
}

const formatGroup = (value: unknown): string => {
    if (!isPlainObject(value)) {
        return NULL_VALUE;
    }

    const { name, options } = value as VariantGroup;
    const optionNames = (options ?? []).map((option) => option?.name).filter(Boolean);
    return optionNames.length > 0 ? `${name}: ${optionNames.join(", ")}` : name ?? NULL_VALUE;
};

const formatGroups = (value: unknown): string => {
    if (Array.isArray(value)) {
        return value.length > 0 ? value.map((group) => formatGroup(group)).join("; ") : NULL_VALUE;
    }

    return formatGroup(value);
};

const baseRow = (audit: Audit, changeLabel: string, from: string, to: string): LedgerRow => ({
    auditId: audit.id,
    objectId: audit.objectId,
    date: audit.createdAt,
    userId: audit.createdBy?.id,
    changeLabel,
    from,
    to,
});

// A creation audit is a whole-record doc: `{ old: null, new: {...} }` at the top level. Check the
// values, not just the keys, so a removal/restore doc (non-null `old`) isn't mislabelled "Created".
export const isCreationAudit = (audit: Audit): boolean => {
    const data = audit?.data;
    if (!isPlainObject(data)) {
        return false;
    }

    const { old, new: next } = data as { old?: unknown; new?: unknown };
    return isNil(old) && !isNil(next);
};

// A soft-delete audit carries a `deletedAt` field transition (old null → new timestamp); its data is
// just deletedAt/deletedBy, both hidden fields, so without this it would produce no ledger row at all.
const isDeletionAudit = (audit: Audit): boolean => {
    const deletedAt = isPlainObject(audit?.data) ? (audit.data as Record<string, unknown>).deletedAt : undefined;
    return isPlainObject(deletedAt) && !isNil((deletedAt as { new?: unknown }).new);
};

// Single "Created" row for a creation audit; state/quantity/reason columns stay empty.
export const createdLedgerRow = (audit: Audit): LedgerRow => baseRow(audit, "Created", NULL_VALUE, NULL_VALUE);

// Single "Deleted" row for a soft-delete audit.
export const deletedLedgerRow = (audit: Audit): LedgerRow => baseRow(audit, "Deleted", NULL_VALUE, NULL_VALUE);

// variantGroups has three shapes: `{old, new}` (add the first group), indexed `{ "1": {old, new} }`
// (add another group), and indexed `{ "0": { options: { "0": {old, new} }, id } }` (option reorder).
// Flatten each into readable "Name: opt, opt" rows.
const variantGroupsToRows = (audit: Audit, change: Record<string, unknown>): LedgerRow[] => {
    if ("old" in change || "new" in change) {
        const from = formatGroups(change.old);
        const to = formatGroups(change.new);
        return from === to ? [] : [baseRow(audit, "Variant Groups", from, to)];
    }

    const rows: LedgerRow[] = [];
    for (const entry of Object.values(change)) {
        if (!isPlainObject(entry)) {
            continue;
        }

        const group = entry as { old?: unknown; new?: unknown; options?: Record<string, unknown> };

        if ("old" in group || "new" in group) {
            const from = formatGroup(group.old);
            const to = formatGroup(group.new);
            if (from !== to) {
                rows.push(baseRow(audit, "Variant Group", from, to));
            }

            continue;
        }

        const options = group.options;
        if (options && typeof options === "object") {
            for (const optionChange of Object.values(options)) {
                const option = optionChange as { old?: { name?: string }; new?: { name?: string } } | null;
                const from = option?.old?.name ?? NULL_VALUE;
                const to = option?.new?.name ?? NULL_VALUE;
                if (from !== to) {
                    rows.push(baseRow(audit, "Variant Option", from, to));
                }
            }
        }
    }

    return rows;
};

const makeRow = (audit: Audit, def: FieldDef | undefined, key: string, old: unknown, next: unknown): LedgerRow => {
    const format = def?.format ?? formatValue;
    return baseRow(audit, def?.label ?? startCase(key), format(old), format(next));
};

interface ArrayFieldLabels {
    summary: string; // label for a whole-array replacement, e.g. "Photos"
    added: string; // label for one added element, e.g. "Photo Added"
    removed: string; // label for one removed element, e.g. "Photo Removed"
    format: (value: unknown) => string; // whole-array summary formatter
    valueOf: (element: unknown) => string; // per-element display, e.g. "Image" or an attachment title
}

// Array fields (medias, attachments) arrive two ways: a whole-array set `{ old:[...], new:[...] }`
// (summarised into one before/after row), or an indexed per-element diff `{ "<i>": { old, new } }` once
// elements are added or removed after the first set. The indexed shape has no top-level old/new, so
// without this it falls through the generic field loop and every incremental add/remove is silently
// dropped, leaving only the initial set visible.
const arrayFieldToRows = (audit: Audit, change: Record<string, unknown>, labels: ArrayFieldLabels): LedgerRow[] => {
    if ("old" in change || "new" in change) {
        const from = labels.format(change.old);
        const to = labels.format(change.new);
        return from === to ? [] : [baseRow(audit, labels.summary, from, to)];
    }

    const rows: LedgerRow[] = [];
    for (const entry of Object.values(change)) {
        if (!isPlainObject(entry)) {
            continue;
        }

        const element = entry as { old?: unknown; new?: unknown };
        const hadOld = !isNil(element.old);
        const hasNew = !isNil(element.new);
        if (hadOld === hasNew) {
            continue; // unchanged, or an in-place swap we can't meaningfully diff
        }

        rows.push(
            baseRow(
                audit,
                hasNew ? labels.added : labels.removed,
                hadOld ? labels.valueOf(element.old) : NULL_VALUE,
                hasNew ? labels.valueOf(element.new) : NULL_VALUE,
            ),
        );
    }

    return rows;
};

const MEDIA_LABELS: ArrayFieldLabels = {
    summary: "Photos",
    added: "Photo Added",
    removed: "Photo Removed",
    format: formatMedias,
    valueOf: () => "Image",
};

const attachmentTitle = (element: unknown): string =>
    isPlainObject(element) && (element as { title?: string }).title
        ? (element as { title: string }).title
        : "Attachment";

const ATTACHMENT_LABELS: ArrayFieldLabels = {
    summary: "Attachments",
    added: "Attachment Added",
    removed: "Attachment Removed",
    format: formatAttachments,
    valueOf: attachmentTitle,
};

// A photo value is an object; identity is its id (or src). Compare identities so a swap to a different
// photo still emits a row, and skip a no-op. Both registries need this, otherwise a variant's primary
// photo falls to the default formatter and renders a raw JSON dump.
const photoIdentity = (value: unknown): string =>
    isPlainObject(value)
        ? String((value as { id?: string; src?: string }).id ?? (value as { src?: string }).src ?? "")
        : "";

const photoToRows = (audit: Audit, change: Record<string, unknown>): LedgerRow[] => {
    if ("old" in change || "new" in change) {
        if (photoIdentity(change.old) === photoIdentity(change.new)) {
            return [];
        }

        return [baseRow(audit, "Primary Photo", change.old ? "Image" : NULL_VALUE, change.new ? "Image" : NULL_VALUE)];
    }

    // Sub-field diff of an existing photo (e.g. `{ src: { old, new } }`): the photo changed in place.
    return Object.keys(change).length > 0 ? [baseRow(audit, "Primary Photo", "Image", "Image")] : [];
};

/**
 * Turn a single ware audit document into ledger rows, one row per changed field.
 * Creation docs (top-level `old === null`) produce no rows (hidden by design).
 * `fields` selects the registry (product-level by default; pass INVENTORY_FIELDS for inventory).
 */
export const auditToLedgerRows = (audit: Audit, fields: Record<string, FieldDef> = LEDGER_FIELDS): LedgerRow[] => {
    const data = audit?.data;
    if (!isPlainObject(data)) {
        return [];
    }

    // Creation docs are rendered as a single "Created" row by the caller, not expanded field-by-field.
    if (isCreationAudit(audit)) {
        return [];
    }

    // A soft-delete would otherwise vanish (its only fields are hidden), so surface one "Deleted" row.
    if (isDeletionAudit(audit)) {
        return [deletedLedgerRow(audit)];
    }

    const rows: LedgerRow[] = [];

    for (const [key, change] of Object.entries(data as Record<string, { old?: unknown; new?: unknown }>)) {
        if (!isPlainObject(change)) {
            continue;
        }

        // variantGroups has bespoke nested/indexed shapes, flatten separately.
        if (key === "variantGroups") {
            rows.push(...variantGroupsToRows(audit, change as Record<string, unknown>));
            continue;
        }

        // medias / attachments are arrays with the same indexed-diff shape; flatten per element.
        if (key === "medias") {
            rows.push(...arrayFieldToRows(audit, change as Record<string, unknown>, MEDIA_LABELS));
            continue;
        }
        if (key === "attachments") {
            rows.push(...arrayFieldToRows(audit, change as Record<string, unknown>, ATTACHMENT_LABELS));
            continue;
        }

        // photo is a single object; handle in both registries so a variant primary photo doesn't fall to
        // the default formatter (raw JSON) and a swap to a different photo still records an event.
        if (key === "photo") {
            rows.push(...photoToRows(audit, change as Record<string, unknown>));
            continue;
        }

        // prices carries the real per-unit change. Two shapes: an update is
        // `{ perUnit: { old, new } }`; an initial set wraps the whole prices object as
        // `{ old, new }` with each side `{ perUnit: { price } }`. Normalize to { old, new } perUnit
        // values and emit a single "Price" row. (Sibling pricePerUnit field's old side is always null.)
        if (key === "prices") {
            const priceChange = change as Record<string, unknown>;
            const perUnit = isPlainObject(priceChange.perUnit)
                ? (priceChange.perUnit as { old?: unknown; new?: unknown })
                : {
                      old: (priceChange.old as { perUnit?: unknown } | null)?.perUnit,
                      new: (priceChange.new as { perUnit?: unknown } | null)?.perUnit,
                  };
            const row = makeRow(
                audit,
                { label: "Price", format: formatPricePerUnit },
                "price",
                perUnit.old,
                perUnit.new,
            );
            if (row.from !== row.to) {
                rows.push(row);
            }

            continue;
        }

        if (!("new" in change || "old" in change)) {
            continue;
        }

        const def = fields[key];
        if (def?.hidden) {
            continue;
        }

        const oldValue = change.old;
        const newValue = change.new;

        // Skip the initial default-set (old === null) for fields flagged hideWhenOldNull (e.g.
        // oversellPolicy gets a default at variant creation, noise). Real later changes still show.
        if (def?.hideWhenOldNull && isNil(oldValue)) {
            continue;
        }

        // Nested object with a sub-field registry (e.g. warePreference): diff per sub-field.
        // Either side may be null (first write / removal), treat as an empty object so the
        // change still expands to per-field rows instead of a JSON.stringify dump.
        if (def?.fields && (isPlainObject(oldValue) || isPlainObject(newValue))) {
            const oldObj = (isPlainObject(oldValue) ? oldValue : {}) as Record<string, unknown>;
            const newObj = (isPlainObject(newValue) ? newValue : {}) as Record<string, unknown>;
            const subKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

            for (const subKey of subKeys) {
                const subRow = makeRow(audit, def.fields[subKey], subKey, oldObj[subKey], newObj[subKey]);
                if (subRow.from !== subRow.to) {
                    rows.push(subRow);
                }
            }

            continue;
        }

        // Compare formatted output, not raw refs: object/array values are always !== by ref
        // and would emit no-op rows (e.g. "2 photos → 2 photos" when both sides format alike).
        const row = makeRow(audit, def, key, oldValue, newValue);
        if (row.from !== row.to) {
            rows.push(row);
        }
    }

    return rows;
};

// Signed delta for a `{ old, new }` numeric change, or undefined if the field isn't in the doc.
const numericDelta = (change: unknown): number | undefined => {
    if (!isPlainObject(change)) {
        return undefined;
    }

    const { old, new: next } = change as { old?: unknown; new?: unknown };
    if (typeof old !== "number" && typeof next !== "number") {
        return undefined;
    }

    const delta = Number(next) - Number(old);
    return Number.isNaN(delta) ? undefined : delta;
};

const AVAILABLE = "Available";
const UNAVAILABLE = "Unavailable";
const RESERVED = "Reserved";
const FULFILLED = "Fulfilled";

// Inventory-lot audits (objectClass ware_inventory_lot) carry raw count/available/unavailable/
// reserved/fulfilled/returned deltas. Interpret the combination as a single stock-movement event
// with a From/To state and a quantity, instead of one raw row per field.
//
// Precedence matters: `reserved` moves in lockstep with `purchased`/`returned`/`pendingFulfillment`,
// so the more specific signals (returned, fulfilled) are checked before the generic reserved delta.
const lotAuditToLedgerRows = (audit: Audit): LedgerRow[] => {
    const data = audit?.data;
    if (!isPlainObject(data)) {
        return [];
    }

    const d = data as Record<string, unknown>;
    const countDelta = numericDelta(d.count);
    const unavailableDelta = numericDelta(d.unavailable);
    const reservedDelta = numericDelta(d.reserved);
    const fulfilledDelta = numericDelta(d.fulfilled);
    const returnedDelta = numericDelta(d.returned);

    // Category rides on each event, not derived from its label, so renaming a label can't silently flip
    // a row's category. Purchase-driven events (reserved/fulfilled/returned) vs manual count moves.
    let event:
        | { changeLabel: string; from: string; to: string; quantity: number; category: LedgerCategory }
        | undefined;
    const purchase = "Purchase" as const;
    const adjustment = "Inventory Adjustment" as const;

    // Deltas are reverse-engineered from the audit: the audit records raw bucket changes (count,
    // unavailable, reserved, fulfilled, returned), not a labeled event. We infer which real-world
    // action produced them and reconstruct the from -> to movement + quantity for display.
    if (returnedDelta !== undefined && returnedDelta > 0) {
        event = { changeLabel: "Returned", from: RESERVED, to: AVAILABLE, quantity: returnedDelta, category: purchase };
    } else if (fulfilledDelta !== undefined && fulfilledDelta > 0) {
        event = {
            changeLabel: "Fulfillment Status",
            from: RESERVED,
            to: FULFILLED,
            quantity: fulfilledDelta,
            category: purchase,
        };
    } else if (reservedDelta !== undefined && reservedDelta > 0) {
        event = {
            changeLabel: "Purchased",
            from: AVAILABLE,
            to: RESERVED,
            quantity: reservedDelta,
            category: purchase,
        };
    } else if (reservedDelta !== undefined && reservedDelta < 0) {
        event = {
            changeLabel: "Decrease Quantity",
            from: RESERVED,
            to: AVAILABLE,
            quantity: -reservedDelta,
            category: purchase,
        };
    } else if (countDelta !== undefined && countDelta > 0) {
        event = {
            changeLabel: "Add Stock",
            from: NULL_VALUE,
            to: AVAILABLE,
            quantity: countDelta,
            category: adjustment,
        };
    } else if (countDelta !== undefined && countDelta < 0) {
        event =
            unavailableDelta !== undefined && unavailableDelta < 0
                ? {
                      changeLabel: "Remove From Unavailable",
                      from: UNAVAILABLE,
                      to: NULL_VALUE,
                      quantity: -countDelta,
                      category: adjustment,
                  }
                : {
                      changeLabel: "Remove From Available",
                      from: AVAILABLE,
                      to: NULL_VALUE,
                      quantity: -countDelta,
                      category: adjustment,
                  };
    } else if (unavailableDelta !== undefined && unavailableDelta > 0) {
        event = {
            changeLabel: "Move to Unavailable",
            from: AVAILABLE,
            to: UNAVAILABLE,
            quantity: unavailableDelta,
            category: adjustment,
        };
    } else if (unavailableDelta !== undefined && unavailableDelta < 0) {
        event = {
            changeLabel: "Move to Available",
            from: UNAVAILABLE,
            to: AVAILABLE,
            quantity: -unavailableDelta,
            category: adjustment,
        };
    }

    if (!event) {
        return [];
    }

    return [
        {
            ...baseRow(audit, event.changeLabel, event.from, event.to),
            category: event.category,
            quantity: event.quantity,
        },
    ];
};

/**
 * Inventory-level audit doc → ledger rows. Lot audits become interpreted stock-movement events;
 * catalog-item audits use the inventory registry (with the derived `available` aggregate hidden).
 * Each row is tagged with the lot's `data.reason` (e.g. "Safety Stock", "Received").
 */
export const inventoryAuditToLedgerRows = (audit: Audit): LedgerRow[] => {
    const rows = inventoryRows(audit);
    const reason = audit.data?.reason?.new;
    const comment = audit.data?.comment?.new;
    if (!reason && !comment) {
        return rows;
    }

    return rows.map((row) => ({ ...row, ...(reason && { reason }), ...(comment && { comment }) }));
};

const inventoryRows = (audit: Audit): LedgerRow[] => {
    // Purchase-item audits (order side: quantity/amount/fulfillmentStatus/units) ride along on the
    // ware-context query. Their inventory effect is already captured by the sibling lot audit
    // (Purchased/Fulfillment/Returned), so skip them here to avoid duplicate/junk rows.
    if (audit.objectClass === PurchaseItemObject.WARE) {
        return [];
    }

    // Lot audits (context query) → interpreted stock-movement events with their own category.
    if (audit.objectClass === WARE_INVENTORY_LOT) {
        return lotAuditToLedgerRows(audit);
    }

    // Catalog-item audits (objectId query) → field edits, category "Catalog".
    if (audit.objectClass === CatalogItemObject.WARE_CATALOG_ITEM) {
        // Creation doc: surface a single "Created" row instead of hiding it.
        if (isCreationAudit(audit)) {
            return [{ ...createdLedgerRow(audit), category: "Catalog" as const }];
        }

        return auditToLedgerRows(audit, CATALOG_ITEM_FIELDS).map((row) => ({ ...row, category: "Catalog" as const }));
    }

    return auditToLedgerRows(audit, INVENTORY_FIELDS);
};
